import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { Loader, ShieldAlert } from 'lucide-react';
import api from '../../utils/axios';
import organizationsApi from '../../utils/organizationsApi';
import InviteSendPanel from './InviteSendPanel';
import JoinRequestQueue from './JoinRequestQueue';

const MUTED = '#64748B';
const APPROVER_ROLES = new Set(['owner', 'admin']);

/**
 * In-app Team management page (rendered inside SidebarLayout).
 *
 * Determines the caller's ACTIVE org and role from the profile
 * (`active_organization`) + memberships list. Owner/admins get the invite
 * composer and the incoming join-request review queue; everyone else sees a
 * read-only notice (and the backend independently 404s the incoming queue).
 *
 * The memberships/profile load runs once on mount (unmount-guarded); it does
 * not depend on callback props, so there is no callback-driven refetch loop.
 */
function TeamPage() {
  const [loading, setLoading] = useState(true);
  const [activeOrgId, setActiveOrgId] = useState(null);
  const [activeMembership, setActiveMembership] = useState(null);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([api.get('/accounts/profile/').then((r) => r.data), organizationsApi.listMemberships()])
      .then(([profile, memberships]) => {
        if (cancelled) return;
        const orgId = profile?.active_organization || null;
        const list = Array.isArray(memberships) ? memberships : [];
        const active =
          list.find((m) => m.organization_id === orgId && m.status === 'active') ||
          list.find((m) => m.status === 'active') ||
          null;
        setActiveOrgId(active?.organization_id || orgId);
        setActiveMembership(active);
      })
      .catch(() => {
        if (!cancelled) setLoadError('Could not load your team.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isApprover = !!activeMembership && APPROVER_ROLES.has(activeMembership.role);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: MUTED, p: 'var(--ab-space-6)', fontSize: 14 }}>
        <Loader size={16} style={{ animation: 'spin 0.8s linear infinite' }} /> Loading team…
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto', px: { xs: 0, md: 'var(--ab-space-4)' }, py: 'var(--ab-space-4)' }}>
      <h1 style={{ fontSize: 32, fontWeight: 700, margin: '0 0 8px', color: '#000' }}>Team</h1>
      <p style={{ fontSize: 15, color: MUTED, margin: '0 0 40px', lineHeight: 1.5 }}>
        {activeMembership?.name
          ? `Manage members and access for ${activeMembership.name}.`
          : 'Manage members and access for your organization.'}
      </p>

      {loadError && (
        <div
          style={{
            border: '1px solid #FF0004',
            background: '#FFF5F5',
            color: '#000',
            padding: '12px 14px',
            fontSize: 13,
            marginBottom: 24,
          }}
        >
          {loadError}
        </div>
      )}

      {isApprover ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 56 }}>
          <InviteSendPanel organizationId={activeOrgId} orgName={activeMembership?.name} />
          <JoinRequestQueue organizationId={activeOrgId} />
        </div>
      ) : (
        <div
          style={{
            border: '1px dashed #A0ABBB',
            padding: '28px 24px',
            display: 'flex',
            gap: 14,
            alignItems: 'flex-start',
            maxWidth: 560,
          }}
        >
          <span style={{ color: MUTED, flexShrink: 0, marginTop: 2 }}>
            <ShieldAlert size={20} />
          </span>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#000' }}>You&rsquo;re a member of this team</div>
            <p style={{ fontSize: 14, color: MUTED, margin: '6px 0 0', lineHeight: 1.5 }}>
              Only owners and admins can invite people or review join requests. Contact an admin if you need to add a
              colleague.
            </p>
          </div>
        </div>
      )}
    </Box>
  );
}

export default TeamPage;
